<template>
  <div class="scan-stock-query-container">
    <!-- 头部导航 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">库存查询</h1>
    </header>

    <!-- 主内容区 -->
    <main class="content">
      <!-- 状态提示横幅 -->
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <!-- 1. 源数据输入区（扫码/手动输入） -->
      <div class="form-section source-section">
        <h3 class="section-title">查询条件输入</h3>
        <div class="form-group">
          <input 
            id="sourceDataInput"
            v-model="sourceData"
            placeholder="扫描数据后自动解析（格式：物料编码*供应商编码）"
            @input="handleInputChange"
            autocomplete="off"
          />
        </div>
        
        <!-- 操作按钮组 -->
        <div class="source-actions">
          <button @click="handleSourceParse" class="btn parse-btn" :disabled="isParsing">
            <i class="icon-parse"></i> {{ isParsing ? '解析中...' : '解析查询' }}
          </button>
          <button @click="openManualInput" class="btn manual-input-btn">
            <i class="icon-manual"></i> 手动输入
          </button>
          <button @click="clearSourceData" class="btn clear-btn">
            <i class="icon-clear"></i> 清空
          </button>
        </div>
      </div>

      <!-- 2. 查询结果展示区 -->
      <div class="form-section result-section">
        <h3 class="section-title">库存查询结果</h3>
        
        <!-- 物料编码汇总区域 -->
        <div v-if="groupedStockByMaterial.length > 0" class="material-summary-section">
          <div class="summary-title">物料编码汇总</div>
          <div class="material-summary-container">
            <table class="summary-table">
              <thead>
                <tr>
                  <th class="summary-header">物料编码</th>
                  <th class="summary-header">总库存</th>
                </tr>
              </thead>
              <tbody>
                <tr class="summary-row" v-for="(item, index) in groupedStockByMaterial" :key="`material-${index}`">
                  <td class="summary-cell">{{ item.materialNo || '-' }}</td>
                  <td class="summary-cell text-primary font-medium">{{ item.totalStockNum || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 查询条件概览（已选择的物料+供应商） -->
        <div v-if="currentQueryCondition" class="query-condition-bar">
          <button @click="clearQueryResult" class="reset-btn">重置查询</button>
        </div>

        <!-- 加载中状态 -->
        <div v-if="isQuerying" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">正在查询库存数据，请稍候...</p>
        </div>

        <!-- 空状态（未查询/无结果） -->
        <div v-else-if="!currentQueryCondition || stockList.length === 0" class="empty-state">
          <div class="empty-icon">📊</div>
          <p class="empty-text">
            {{ currentQueryCondition ? '未查询到对应库存数据' : '请扫描或手动输入查询条件' }}
          </p>
        </div>

        <!-- 库存列表结果 -->
        <div v-else class="stock-list-container">
          <!-- 按供应商分组显示 -->
        <div v-for="(group, key) in groupedStockBySupplier" :key="key" class="supplier-group">
          <!-- 供应商分组标题 -->
          <div class="supplier-group-header">
            <h3 class="supplier-group-title">{{ group.supplierName }}</h3>
            <p class="supplier-group-count">共 {{ group.stocks.length }} 条记录</p>
          </div>

          <!-- 供应商库存表格 -->
          <div class="stock-table-container">
            <table class="stock-table">
              <thead>
                <tr>
                  <th class="table-header">物料编码</th>
                  <th class="table-header">总库存</th>
                  <!-- <th class="table-header">良品</th> -->
                  <!-- <th class="table-header">不良品</th> -->
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" 
                  v-for="(item, index) in group.stocks" 
                  :key="`stock-${item.id || index}`"
                  @click="showDetail(item)"
                  style="cursor: pointer;">
                  <td class="table-cell">{{ item.materialNo || '-' }}</td>
                  <td class="table-cell text-primary font-medium">{{ item.stockNum || 0 }}</td>
                  <!-- <td class="table-cell">{{ item.goodStockNum || 0 }}</td> -->
                  <!-- <td class="table-cell">{{ item.badStockNum || 0 }}</td> -->
                </tr>
                <tr v-if="group.stocks.length === 0">
                  <td colspan="4" class="empty-table">该供应商暂无库存数据</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 分页控件 -->
        <div v-if="totalStockCount > pageSize" class="pagination-container">
          <button 
            @click="changePage(currentPage - 1)" 
            class="page-btn" 
            :disabled="currentPage <= 1"
          >
            上一页
          </button>
          <span class="page-info">
            第 {{ currentPage }} 页 / 共 {{ Math.ceil(totalStockCount / pageSize) }} 页
          </span>
          <button 
            @click="changePage(currentPage + 1)" 
            class="page-btn" 
            :disabled="currentPage >= Math.ceil(totalStockCount / pageSize)"
          >
            下一页
          </button>
        </div>
        </div>
      </div>
    </main>

    <!-- 3. 手动输入弹窗 -->
    <div v-if="showManualInput" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">手动输入查询条件</h3>
          <button @click="closeManualInput" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="manualMaterialNo">物料编码</label>
            <input 
              id="manualMaterialNo" 
              v-model="manualInput.materialNo" 
              type="text" 
              placeholder="请输入物料编码"
              @input="trimInput('materialNo')"
              @blur="handleMaterialNoBlur"
            >
          </div>
          <div class="form-group">
          <label for="manualSupplierName">供应商名称</label>
          <input 
            id="manualSupplierName" 
            v-model="manualInput.supplierName" 
            type="text" 
            placeholder="请输入供应商名称"
            @input="trimInput('supplierName')"
            @blur="handleSupplierNameBlur"
          >
        </div>
        </div>
        <div class="modal-footer">
          <button @click="closeManualInput" class="btn modal-cancel">取消</button>
          <button @click="confirmManualQuery" class="btn modal-confirm" :disabled="!isManualInputValid">
            确认查询
          </button>
        </div>
      </div>
    </div>

    <!-- 4. 错误提示弹窗（供应商/物料不存在） -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ errorModalTitle }}</h3>
          <button @click="showErrorModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ errorModalDetail }}</p>
          <p class="error-tip" v-if="errorModalAdvice">{{ errorModalAdvice }}</p>
        </div>
        <div class="modal-footer">
          <button @click="showErrorModal = false" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>

    <!-- 6. 库存详情弹窗 -->
    <div v-if="showDetailModal" class="modal-overlay">
      <div class="modal-container" style="width: 90%; max-width: 600px;">
        <div class="modal-header">
          <h3 class="modal-title">物料批次详情</h3>
          <button @click="closeDetailModal" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body" style="max-height: 70vh; overflow-y: auto;">
          <!-- 批次详情表格 -->
          <div class="detail-table-container">
            <table class="detail-table bordered-table">
              <thead>
                <tr>
                  <!-- <th class="detail-header">托盘名称</th> -->
                  <th class="detail-header">批次号</th>
                  <th class="detail-header">库存</th>
                </tr>
              </thead>
              <tbody>
                <tr class="detail-row" v-for="(item, index) in currentDetailList" :key="`detail-${item.id || index}`">
                  <!-- <td class="detail-cell">{{ item.palletCode || '-' }}</td> -->
                   <td class="detail-cell">{{ item.batchNo || '-' }}</td>
                  <td class="detail-cell">{{ item.stockNum || 0 }}</td>
                </tr>
                <tr v-if="currentDetailList.length === 0">
                  <td colspan="4" class="empty-detail">暂无批次详情数据</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDetailModal" class="btn modal-confirm">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
// 修改为导入新创建的库存查询API
import * as stockApi from '@/api/stockQuery'
// import { getSuppliers } from '@/api/stockIn'



export default {
  name: 'ScanStockQuery',
  setup() {
    const router = useRouter()

    // -------------------------- 基础状态管理 --------------------------
    // 音频上下文（提示音）
    let audioContext = null
    const isAudioContextActive = ref(false)

    // 扫码输入相关
    const sourceData = ref('') // 扫码输入框内容
    const isParsing = ref(false) // 解析中状态
    const statusMsg = ref('') // 状态提示文本
    const statusType = ref('success') // 状态类型：success/error/warn

    // 弹窗控制
    const showManualInput = ref(false) // 手动输入弹窗
    const showErrorModal = ref(false) // 错误提示弹窗

    // 错误弹窗内容
    const errorModalTitle = ref('提示')
    const errorModalDetail = ref('')
    const errorModalAdvice = ref('')

    // -------------------------- 库存详情弹窗相关 --------------------------
    const showDetailModal = ref(false) // 详情弹窗控制
    const currentStockItem = ref(null) // 当前选中的库存项
    const currentDetailList = ref([]) // 当前库存项的批次详情列表

    // -------------------------- 库存查询相关 --------------------------
    const currentQueryCondition = ref(null) // 当前查询条件（物料+供应商）
        const stockList = ref([]) // 库存列表数据
        const isQuerying = ref(false) // 查询中状态
        const totalStockCount = ref(0) // 库存总记录数
        const currentPage = ref(1) // 当前页码
        const pageSize = ref(20) // 每页条数

    // 手动输入表单
    const manualInput = ref({
      materialNo: '', // 物料编码
      supplierCode: '', // 供应商编码
      supplierName: '', // 供应商名称（联动查询）
      supplierId: '' // 供应商ID（用于接口参数）
    })

    // -------------------------- 计算属性 --------------------------
    // 手动输入表单验证
    const isManualInputValid = computed(() => {
      // 修改为：允许空值提交（移除必填项验证）
      return true;
    })

    // 库存统计：总数量
    const totalStockQuantity = computed(() => {
      return stockList.value.reduce((total, item) => total + (Number(item.stockNum) || 0), 0)
    })

    // 新增：良品总数量
    const totalGoodQuantity = computed(() => {
      return stockList.value.reduce((total, item) => total + (Number(item.goodStockNum) || 0), 0)
    })

    // 新增：不良品总数量
    const totalBadQuantity = computed(() => {
      return stockList.value.reduce((total, item) => total + (Number(item.badStockNum) || 0), 0)
    })

    // 新增：按物料编码分组的库存数据
    const groupedStockByMaterial = computed(() => {
      const groups = stockList.value.reduce((acc, item) => {
        const materialNo = item.materialNo || '未知物料'
        if (!acc[materialNo]) {
          acc[materialNo] = {
            materialNo: item.materialNo,
            materialName: item.materialName,
            totalStockNum: 0,
            totalGoodStockNum: 0,
            totalBadStockNum: 0
          }
        }
        acc[materialNo].totalStockNum += Number(item.stockNum) || 0
        acc[materialNo].totalGoodStockNum += Number(item.goodStockNum) || 0
        acc[materialNo].totalBadStockNum += Number(item.badStockNum) || 0
        return acc
      }, {})
      
      // 转换为数组并按物料编码排序
      return Object.values(groups).sort((a, b) => {
        if (a.materialNo && b.materialNo) {
          return a.materialNo.localeCompare(b.materialNo)
        }
        return 0
      })
    })

    // 新增：获取唯一供应商列表
    const uniqueSuppliers = computed(() => {
      // 从库存列表中提取所有非空供应商名称并去重
      const suppliers = new Set(
        stockList.value
          .map(item => item.supplierName)
          .filter(Boolean) // 过滤空值
      )
      return Array.from(suppliers)
    })

    // 新增：按供应商分组的库存数据
    const groupedStockBySupplier = computed(() => {
      return stockList.value.reduce((groups, item) => {
        // 使用供应商ID作为分组键，确保唯一性
        const key = item.supplierId || item.supplierName
        if (!groups[key]) {
          groups[key] = {
            supplierId: item.supplierId,
            supplierName: item.supplierName,
            stocks: []
          }
        }
        groups[key].stocks.push(item)
        return groups
      }, {})
    })

    // -------------------------- 工具函数 --------------------------
    /**
     * 初始化音频上下文（解决移动端自动播放限制）
     */
    const initAudioContext = () => {
      if (audioContext || isAudioContextActive.value) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        audioContext = new AudioContextConstructor()
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            isAudioContextActive.value = true
          }).catch(err => console.warn('激活音频上下文失败：', err))
        } else {
          isAudioContextActive.value = true
        }

        // 仅初始化一次
        document.removeEventListener('click', initAudioContext)
        document.removeEventListener('touchstart', initAudioContext)
      } catch (err) {
        console.warn('创建音频上下文失败：', err)
      }
    }

    /**
     * 播放错误提示音
     */
    const playErrorBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(2200, audioContext.currentTime)
        gainNode.gain.setValueAtTime(2, audioContext.currentTime)

        // 3次短音提示
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            gainNode.gain.setValueAtTime(2, audioContext.currentTime)
            setTimeout(() => gainNode.gain.setValueAtTime(0, audioContext.currentTime), 100)
          }, i * 200)
        }

        oscillator.start()
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    }

    // -------------------------- 库存详情方法 --------------------------
    /**
     * 显示库存详情弹窗
     * @param {Object} item - 库存项数据
     */
    const showDetail = (item) => {
      currentStockItem.value = item
      currentDetailList.value = item.materialStockDetailList || []
      showDetailModal.value = true
    }

    /**
     * 关闭详情弹窗
     */
    const closeDetailModal = () => {
      showDetailModal.value = false
      currentStockItem.value = null
      currentDetailList.value = []
    }

    /**
     * 播放成功提示音
     */
    const playSuccessBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, audioContext.currentTime)

        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.3)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    }

    /**
     * 设置状态提示
     * @param {string} message - 提示内容
     * @param {string} type - 提示类型（success/error/warn）
     */
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) statusMsg.value = ''
      }, 3000)
    }

    /**
     * 格式化日期（YYYY-MM-DD）
     * @param {string|number} dateStr - 日期字符串/时间戳
     * @returns {string} 格式化后的日期
     */
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return ''
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    }

    // -------------------------- 扫码解析逻辑 --------------------------
    /**
     * 处理扫码输入变化（自动触发解析）
     */
    let parseTimer = null
    const handleInputChange = () => {
      if (parseTimer) clearTimeout(parseTimer)
      const currentData = (sourceData.value || '').trim()
      
      // 检测到换行符时立即解析单条数据
      if (currentData.includes('\n') || currentData.includes('\r')) {
        const lineBreakMatch = currentData.match(/[\n\r]/)
        if (lineBreakMatch) {
          const firstData = currentData.substring(0, lineBreakMatch.index).trim()
          if (firstData) {
            if (parseTimer) clearTimeout(parseTimer)
            sourceData.value = currentData.substring(lineBreakMatch.index + 1).trim()
            handleSourceParse(firstData)
          }
        }
      }
      // 长度触发解析（使用与出库相同的15字符阈值和300ms延迟）
      else if (currentData.length > 15) {
        parseTimer = setTimeout(() => {
          handleSourceParse()
        }, 300) // 与出库保持一致的300ms延迟
      }
    }

    /**
     * 解析扫码数据并查询库存
     * @param {string|null} singleData - 单条扫码数据
     */
    const handleSourceParse = async (singleData = null) => {
      if (isParsing.value) return
      isParsing.value = true

      try {
        await nextTick()
        const cleanData = singleData ? singleData.trim() : sourceData.value.trim()
        if (!cleanData) {
          setStatus('请输入有效的扫描数据', 'warn')
          return
        }

        // 解析扫码数据（多字段格式处理）
        const fields = cleanData.split('*').map(item => item.trim())
        
        // 验证基本格式（至少需要物料编码、数量、供应商编码）
        if (fields.length < 3 || !fields[0] || !fields[1] || !fields[2]) {
          setStatus('数据格式错误，应为：物料编码*数量*供应商编码*...*批次号', 'error')
          playErrorBeep()
          return
        }

        // 提取核心字段
        const materialNo = fields[0]
        const quantity = fields[1]
        const supplierCode = fields[2]
        
        // 提取批次号（最后一个非空字段）
        const batchNo = fields.reduceRight((last, current) => {
          return current ? current : last
        }, '')

        // 验证供应商是否存在
        const supplierRes = await stockApi.getSupplierByCode(supplierCode)
        const supplierList = supplierRes.data?.records || []
        if (supplierList.length === 0) {
          showErrorModal.value = true
          errorModalDetail.value = `供应商编码「${supplierCode}」不存在`
          errorModalAdvice.value = '请检查供应商编码是否正确'
          playErrorBeep()
          return
        }

        // 执行库存查询
        currentQueryCondition.value = {
          materialNo,
          supplierCode,
          supplierId: supplierList[0].id,
          supplierName: supplierList[0].supplierName,
          quantity, // 新增：数量
          batchNo    // 新增：批次号
        }
        
        // 重置页码并查询
        currentPage.value = 1
        await fetchStockData()
        playSuccessBeep()
        
        // 清空输入框（仅当使用输入框内容时）
        if (!singleData) sourceData.value = ''
        document.getElementById('sourceDataInput')?.focus()
      } catch (err) {
        console.error('解析查询失败：', err)
        setStatus(`解析失败：${err.message || '系统异常'}`, 'error')
        playErrorBeep()
      } finally {
        isParsing.value = false
      }
    }

    // -------------------------- 库存查询逻辑 --------------------------
    /**
     * 调用库存查询接口
     */
    const fetchStockData = async () => {
      isQuerying.value = true

      try {
        // 解构出所有需要的参数，包括 supplierName
        const { materialNo, supplierId, supplierName } = currentQueryCondition.value
        
        // 仅传递有值的参数（避免传递空字符串）
        const params = {
          current: currentPage.value,
          size: pageSize.value
        }
        
        // 添加物料编码参数
        if (materialNo) params.materialNo = materialNo
        
        // 添加供应商ID参数
        if (supplierId) params.supplierId = supplierId
        
        // 关键修复：添加供应商名称参数
        if (supplierName) params.supplierName = supplierName

        const res = await stockApi.queryMaterialStock(params)

        if (res.success) {
          stockList.value = res.data.records || []
          totalStockCount.value = res.data.total || 0
          
          if (stockList.value.length === 0) {
            setStatus('未查询到符合条件的库存数据', 'warn')
          } else {
            setStatus(`查询成功，共找到 ${totalStockCount.value} 条记录`, 'success')
          }
        } else {
          throw new Error(res.msg || '查询库存失败')
        }
      } catch (err) {
        console.error('查询库存数据失败：', err)
        setStatus(`查询失败：${err.message || '网络异常'}`, 'error')
        playErrorBeep()
      } finally {
        isQuerying.value = false
      }
    }

    /**
     * 切换分页
     * @param {number} page - 目标页码
     */
    const changePage = (page) => {
      if (page < 1 || page > Math.ceil(totalStockCount.value / pageSize.value)) return
      currentPage.value = page
      fetchStockData()
      
      // 滚动到表格顶部
      const tableContainer = document.querySelector('.stock-table-container')
      tableContainer?.scrollIntoView({ behavior: 'smooth' })
    }

    // -------------------------- 手动输入逻辑 --------------------------
    /**
     * 清除输入框空格
     * @param {string} field - 字段名
     */
    const trimInput = (field) => {
      if (typeof manualInput.value[field] === 'string') {
        manualInput.value[field] = manualInput.value[field].trim()
      }
    }

    /**
     * 供应商编码失焦验证
     */
    const handleSupplierCodeBlur = async () => {
      const code = manualInput.value.supplierCode.trim()
      if (!code) {
        manualInput.value.supplierId = ''
        manualInput.value.supplierName = ''
        return
      }

      try {
        const res = await stockApi.getSupplierByCode(code)
        const supplierList = res.data?.records || []
        if (supplierList.length > 0) {
          manualInput.value.supplierId = supplierList[0].id
          manualInput.value.supplierName = supplierList[0].supplierName
        } else {
          manualInput.value.supplierId = ''
          manualInput.value.supplierName = ''
          showErrorModal.value = true
          errorModalDetail.value = `供应商编码「${code}」不存在`
          errorModalAdvice.value = '请检查供应商编码是否正确'
        }
      } catch (err) {
        console.error('查询供应商失败：', err)
        setStatus('查询供应商信息失败，请稍后重试', 'error')
      }
    }

    /**
     * 物料编码失焦验证（仅检查格式）
     */
    const handleMaterialNoBlur = () => {
      const materialNo = manualInput.value.materialNo.trim()
      if (!materialNo) {
        setStatus('物料编码不能为空', 'warn')
      }
    }

    /**
     * 供应商名称失焦查询
     */
    // const handleSupplierNameBlur = async () => {
    //   const name = manualInput.value.supplierName.trim()
    //   if (!name) {
    //     manualInput.value.supplierId = ''
    //     return
    //   }

    //   try {
    //     // 查询供应商列表（支持按名称模糊搜索）
    //     const res = await getSuppliers({
    //       supplierName: name, // 假设API支持name参数模糊查询
    //       pageSize: 10
    //     })

    //     const supplierList = res.data?.records || []
    //     if (supplierList.length === 0) {
    //       setStatus('未找到匹配的供应商，请检查名称是否正确', 'warn')
    //       manualInput.value.supplierId = ''
    //     } else if (supplierList.length === 1) {
    //       // 唯一匹配时自动选择
    //       manualInput.value.supplierId = supplierList[0].id
    //       setStatus(`已匹配供应商：${supplierList[0].supplierName}`, 'success')
    //     } else {
    //       // 多个匹配时提示用户选择
    //       showErrorModal.value = true
    //       errorModalTitle.value = '找到多个供应商'
    //       errorModalDetail.value = `请从以下供应商中选择：\n${supplierList.map(s => s.supplierName).join('\n')}`
    //       errorModalAdvice.value = '请输入更精确的供应商名称'
    //       manualInput.value.supplierId = ''
    //     }
    //   } catch (err) {
    //     console.error('查询供应商失败：', err)
    //     setStatus('查询供应商信息失败，请稍后重试', 'error')
    //   }
    // }

    /**
     * 确认手动查询
     */
    const confirmManualQuery = async () => {
      currentQueryCondition.value = {
        materialNo: manualInput.value.materialNo.trim() || undefined,
        supplierCode: manualInput.value.supplierCode.trim() || undefined,
        supplierId: manualInput.value.supplierId || undefined,
        supplierName: manualInput.value.supplierName
      }

      // 重置页码并查询
      currentPage.value = 1
      await fetchStockData()
      playSuccessBeep()
      showManualInput.value = false
    }

    // -------------------------- 其他操作 --------------------------
    /**
     * 清空扫码输入
     */
    const clearSourceData = () => {
      sourceData.value = ''
      if (parseTimer) clearTimeout(parseTimer)
      document.getElementById('sourceDataInput')?.focus()
    }

    /**
     * 重置查询结果
     */
    const clearQueryResult = () => {
      currentQueryCondition.value = null
      stockList.value = []
      totalStockCount.value = 0
      currentPage.value = 1
      setStatus('已重置查询条件', 'info')
    }

    /**
     * 打开手动输入弹窗
     */
    const openManualInput = () => {
      manualInput.value = {
        materialNo: '',
        supplierCode: '',
        supplierName: '',
        supplierId: ''
      }
      showManualInput.value = true
      setTimeout(() => {
        document.getElementById('manualMaterialNo')?.focus()
      }, 300)
    }

    /**
     * 关闭手动输入弹窗
     */
    const closeManualInput = () => {
      showManualInput.value = false
    }

    /**
     * 返回上一页
     */
    const handleBack = () => {
      router.back()
    }

    // -------------------------- 组件初始化 --------------------------
    onMounted(() => {
      // 输入框自动聚焦
      document.getElementById('sourceDataInput')?.focus()
      
      // 初始化音频上下文（需用户交互触发）
      document.addEventListener('click', initAudioContext, { once: true })
      document.addEventListener('touchstart', initAudioContext, { once: true })
    })

    return {
      // 状态变量
      sourceData,
      isParsing,
      statusMsg,
      statusType,
      showManualInput,
      showErrorModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      currentQueryCondition,
      stockList,
      isQuerying,
      totalStockCount,
      currentPage,
      pageSize,
      manualInput,
      showDetailModal,
      currentStockItem,
      currentDetailList,
      
      // 计算属性
      isManualInputValid,
      totalStockQuantity,
      totalGoodQuantity,
      totalBadQuantity,
      uniqueSuppliers,
      groupedStockBySupplier,
      
      // 方法
      handleInputChange,
      handleSourceParse,
      fetchStockData,
      changePage,
      trimInput,
      handleSupplierCodeBlur,
      handleMaterialNoBlur,
      // handleSupplierNameBlur,
      confirmManualQuery,
      clearSourceData,
      clearQueryResult,
      openManualInput,
      closeManualInput,
      handleBack,
      formatDate,
      playErrorBeep,
     showDetail,
     groupedStockByMaterial,
     closeDetailModal
    }
  }
}
    
</script>

<style scoped>
/* 基础容器样式 */
.scan-stock-query-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.icon-back {
  width: 24px;
  height: 24px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 20px;
}

/* 状态提示样式 */
.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.status-banner.info {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  border: 1px solid var(--border-color);
}

/* 表单区域样式 */
.form-section {
  background: var(--card-background);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.section-title {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: var(--primary-color);
  margin-right: 8px;
  border-radius: 2px;
}

/* 查询条件栏 */
.query-condition-bar {
  padding: 12px;
  background-color: var(--card-background);
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-color);
}

.condition-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.condition-label {
  color: var(--text-secondary);
  font-weight: 500;
  margin-right: 6px;
}

.condition-value {
  color: var(--primary-color);
  font-weight: 600;
}

.reset-btn {
  margin-left: auto;
  background-color: var(--border-color);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  height: 32px;
  white-space: nowrap;
}

.reset-btn:hover {
  background-color: var(--border-color);
}

/* 输入控件样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

#sourceDataInput, .form-group input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 48px;
  background-color: var(--card-background);
  color: var(--text-primary);
}

#sourceDataInput:focus, .form-group input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

/* 供应商名称提示 */
.supplier-name-hint {
  margin: 6px 0 0 0;
  color: #10b981;
  font-size: 13px;
  font-style: italic;
}

/* 按钮样式 */
.btn {
  min-height: 44px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  font-weight: 600;
  transition: background-color 0.2s;
}

/* 按钮布局 */
.source-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.source-actions .parse-btn {
  grid-column: 1 / 2;
}

.source-actions .manual-input-btn {
  grid-column: 2 / 3;
}

.source-actions .clear-btn {
  grid-column: 1 / 3;
}

/* 按钮颜色区分 */
.parse-btn {
  background-color: var(--primary-color);
  color: var(--primary-text);
}

.parse-btn:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

.parse-btn:not(:disabled):hover {
  background-color: var(--primary-color);
}

.manual-input-btn {
  background-color: #f59e0b;
  color: #fff;
}

.manual-input-btn:hover {
  background-color: #d97706;
}

.clear-btn {
  background-color: var(--border-color);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.clear-btn:hover {
  background-color: var(--border-color);
}

/* 库存统计摘要 */
.stock-summary-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background-color: var(--card-background);
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid var(--border-color);
}

.summary-item {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.summary-label {
  color: var(--text-secondary);
  margin-right: 6px;
}

.summary-value {
  color: var(--text-primary);
}

/* 表格容器样式 - 添加水平滚动 */
.stock-table-container {
  overflow-x: auto;
  width: 100%;
}

/* 表格样式 */
.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.stock-table th,
.stock-table td {
  border: 1px solid var(--border-color);
  padding: 8px 12px;
  text-align: left;
}

.stock-table th {
  background-color: var(--card-background);
  font-weight: 500;
  color: var(--text-secondary);
}

.table-header {
  background-color: var(--card-background);
  color: var(--text-secondary);
  font-weight: 600;
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  white-space: nowrap;
}

.table-row {
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: var(--card-background);
}

.table-cell {
  padding: 8px 12px;
  white-space: nowrap; /* 禁止内容换行 */
  color: var(--text-primary);
}

.empty-detail {
  text-align: center !important;
  padding: 24px;
  color: var(--text-secondary);
  background-color: var(--card-background);
}

/* 分页控件 */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  background-color: var(--card-background);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.page-btn {
  background-color: var(--card-background);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:disabled {
  color: var(--text-secondary);
  border-color: var(--border-color);
  cursor: not-allowed;
  background-color: var(--border-color);
}

.page-btn:not(:disabled):hover {
  background-color: var(--primary-color);
  color: var(--primary-text);
}

.page-info {
  color: var(--text-secondary);
  font-size: 14px;
}

/* 空状态样式 */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: var(--text-secondary);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
}

/* 加载中状态 */
.loading-state {
  padding: 48px 0;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-color);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.modal-container {
  width: 100%;
  max-width: 500px;
  background-color: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 4px 24px var(--shadow-light);
  overflow: hidden;
  animation: modalFadeIn 0.3s;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.modal-close:hover {
  background-color: #f5f7fa;
  color: #333;
}

.modal-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
}

.modal-footer .btn {
  flex: 1;
}

.modal-cancel {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.modal-cancel:hover {
  background-color: #eef0f3;
}

.modal-confirm {
  background-color: #1890ff;
  color: #fff;
}

.modal-confirm:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.modal-confirm:not(:disabled):hover {
  background-color: #096dd9;
}

/* 错误提示文本 */
.error-message {
  color: #f5222d;
  font-size: 14px;
  margin: 0 0 8px 0;
  text-align: center;
}

.error-tip {
  color: #999;
  font-size: 13px;
  margin: 0;
  text-align: center;
}

/* 必填项标记 */
.required {
  color: #f5222d;
  margin-left: 2px;
}

/* 工具类 */
.text-primary {
  color: #1890ff;
}

.font-medium {
  font-weight: 500;
}

/* 响应式适配 */
@media (max-width: 480px) {
  .source-actions {
    grid-template-columns: 1fr;
  }
  
  .source-actions .parse-btn,
  .source-actions .manual-input-btn,
  .source-actions .clear-btn {
    grid-column: 1 / 2;
    width: 100%;
  }
  
  .query-condition-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .reset-btn {
    margin-left: 0;
    align-self: flex-end;
    width: 100%;
  }
  
  .stock-summary-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .pagination-container {
    gap: 8px;
  }
  
  .page-btn {
    padding: 4px 8px;
    font-size: 13px;
  }
  
  .modal-body {
    padding: 12px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 物料编码汇总区域样式 */
.material-summary-section {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #495057;
}

.material-summary-container {
  overflow-x: auto;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.summary-header {
  background-color: #e9ecef;
  padding: 8px 12px;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
}

/* 添加表格头部样式 */
.table-header {
  background-color: #e9ecef;
  padding: 8px 12px;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
}

.summary-row {
  border-bottom: 1px solid #dee2e6;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-cell {
  padding: 8px 12px;
  color: #495057;
}

.summary-cell.text-primary {
  color: #007bff;
}

.summary-cell.font-medium {
  font-weight: 500;
}

/* 新增：供应商列表样式 */
.supplier-list-bar {
  margin-bottom: 16px;
  padding: 10px 12px;
  background-color: #f5f7fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.supplier-label {
  color: #666;
  font-size: 14px;
  white-space: nowrap;
}

.supplier-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.supplier-tag {
  background-color: #e8f3ff;
  color: #1890ff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  white-space: nowrap;
}

/* 供应商分组样式 */
.supplier-group {
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.supplier-group-header {
  background-color: #f9fafb;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.supplier-group-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.supplier-group-count {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* 新增：带边框的表格样式 */
.bordered-table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
}

.bordered-table th,
.bordered-table td {
  border: 1px solid #e5e7eb !important;
  padding: 8px 12px;
  text-align: center !important;
}

.bordered-table th {
  background-color: #f3f4f6;
  font-weight: 500;
}
</style>
